/**
 * Copyright © 2023 9Folders, Inc. All rights reserved.
 * TypeScript 변환 버전
 */

// 이름 스타일 상수
export enum FullNameStyle {
  UNDEFINED = 0,
  KOREAN = 1,
  JAPANESE = 2,
  CHINESE = 3,
  KJC = 4, // Korean, Japanese, Chinese
  WESTERN = 5,
}

// 음성 이름 스타일 상수
export enum PhoneticNameStyle {
  UNDEFINED = 0,
  KOREAN = 1,
  JAPANESE = 2,
  CHINESE = 3,
  KCJ = 4, // Korean, Chinese, Japanese
  PINYIN = 5,
}

// 한국어 2글자 성씨 리스트
const KR_TWO_CHAR_FAMILY_NAMES: string[] = [
  '강전', // Gang Jeon
  '남궁', // Nam Goong
  '독고', // Dok Go
  '동방', // Dong Bang
  '망절', // Mang Jeol
  '사공', // Sa Gong
  '서문', // Seo Moon
  '선우', // Seon Woo
  '소봉', // So Bong
  '어금', // Uh Geum
  '장곡', // Jang Gok
  '제갈', // Je Gal
  '황보', // Hwang Bo
]

interface PersonInput {
  prefix?: string | null
  first_name?: string | null
  middle_name?: string | null
  last_name?: string | null
  suffix?: string | null
}

export class NameObj {
  prefix: string | null = null
  givenNames: string | null = null
  middleName: string | null = null
  familyName: string | null = null
  suffix: string | null = null

  fullNameStyle: FullNameStyle = FullNameStyle.UNDEFINED

  phoneticFamilyName: string | null = null
  phoneticMiddleName: string | null = null
  phoneticGivenName: string | null = null

  phoneticNameStyle: PhoneticNameStyle = PhoneticNameStyle.UNDEFINED

  constructor(person: PersonInput | null = null) {
    this.clear()
    if (person) {
      this.set(person)
    }
  }

  set(person: PersonInput): void {
    this.prefix = person.prefix || null
    this.givenNames = person.first_name || null
    this.middleName = person.middle_name || null
    this.familyName = person.last_name || null
    this.suffix = person.suffix || null
  }

  clear(): void {
    this.prefix = null
    this.givenNames = null
    this.middleName = null
    this.familyName = null
    this.suffix = null

    this.fullNameStyle = FullNameStyle.UNDEFINED

    this.phoneticFamilyName = null
    this.phoneticMiddleName = null
    this.phoneticGivenName = null

    this.phoneticNameStyle = PhoneticNameStyle.UNDEFINED
  }

  get isEmpty(): boolean {
    return (
      (!this.givenNames || this.givenNames.length === 0) &&
      (!this.middleName || this.middleName.length === 0) &&
      (!this.familyName || this.familyName.length === 0) &&
      (!this.suffix || this.suffix.length === 0) &&
      (!this.phoneticFamilyName || this.phoneticFamilyName.length === 0) &&
      (!this.phoneticMiddleName || this.phoneticMiddleName.length === 0) &&
      (!this.phoneticGivenName || this.phoneticGivenName.length === 0)
    )
  }
}

class NameTokenizer {
  tokens: string[] = []
  dotBitmask: number = 0
  commaBitmask: number = 0
  startPointer: number = 0
  endPointer: number = 0

  constructor(fullName: string) {
    this.processTokenWithFullName(fullName)
  }

  processTokenWithFullName(fullName: string): void {
    if (!fullName) return

    const MAX_TOKEN = 10
    const tempTokens: string[] = []
    let token = ''

    // 토큰화
    for (let i = 0; i < fullName.length; i++) {
      const ch = fullName.charAt(i)
      if (ch === ' ' || ch === '.' || ch === ',') {
        if (token.length > 0) {
          tempTokens.push(token)
          token = ''
        }
      }
      token += ch
    }
    if (token.length > 0) {
      tempTokens.push(token)
    }

    // 결과 토큰 처리
    const resultTokens: string[] = []
    for (const t of tempTokens) {
      if (this.endPointer >= MAX_TOKEN) break

      let ch = ''
      if (t.length > 0) {
        ch = t.charAt(0)
        if (ch === ' ') continue
      }

      if (this.endPointer > 0 && ch === '.') {
        this.dotBitmask |= 1 << (this.endPointer - 1)
      } else if (this.endPointer > 0 && ch === ',') {
        this.commaBitmask |= 1 << (this.endPointer - 1)
      } else {
        resultTokens.push(t)
        this.endPointer++
      }
    }

    this.tokens = resultTokens
  }

  hasDot(index: number): boolean {
    return (this.dotBitmask & (1 << index)) !== 0
  }

  hasComma(index: number): boolean {
    return (this.commaBitmask & (1 << index)) !== 0
  }
}

export class NameSplitter {
  locale: string
  language: string
  prefixesSet: Set<string>
  suffixesSet: Set<string>
  lastNamePrefixesSet: Set<string>
  conjunctions: Set<string>
  maxSuffixLength: number

  constructor(
    locale: string | null = null,
    commonPrefix: string = '',
    commonSuffix: string = '',
    commonLastNamePrefix: string = '',
    commonConjunction: string = '',
  ) {
    this.locale = locale || navigator.language || 'en-US'
    this.language = this.locale.split('-')[0]

    // 접두사, 접미사, 마지막 이름 접두사, 접속사 집합 설정
    this.prefixesSet = this.convertToSet(commonPrefix)
    this.suffixesSet = this.convertToSet(commonSuffix)
    this.lastNamePrefixesSet = this.convertToSet(commonLastNamePrefix)
    this.conjunctions = this.convertToSet(commonConjunction)

    // 최대 접미사 길이 계산
    this.maxSuffixLength = 0
    for (const suffix of this.suffixesSet) {
      if (suffix.length > this.maxSuffixLength) {
        this.maxSuffixLength = suffix.length
      }
    }
  }

  convertToSet(strings: string): Set<string> {
    const set = new Set<string>()
    if (strings && strings.length > 0) {
      const splits = strings.split(',')
      for (let str of splits) {
        str = str.trim().toUpperCase()
        if (str.length > 0) {
          set.add(str)
        }
      }
    }
    return set
  }

  // 이름 스타일 추측
  guessNameStyle(name: NameObj): void {
    this.guessFullNameStyle(name)
    this.guessPhoneticNameStyle(name)
    name.fullNameStyle = this.adjustedNameStyle(name.fullNameStyle, name.phoneticNameStyle)
  }

  guessFullNameStyle(name: NameObj): void {
    if (name.fullNameStyle !== FullNameStyle.UNDEFINED) {
      return
    }

    let bestGuess = this.guessFullNameStyleWithName(name.givenNames)
    // 한자와 라틴 문자의 혼합은 중국에서 흔하므로, 일본어나 한국어가 아닌 경우 모든 이름을 검사해야 합니다.
    if (
      bestGuess !== FullNameStyle.UNDEFINED &&
      bestGuess !== FullNameStyle.KJC &&
      bestGuess !== FullNameStyle.WESTERN
    ) {
      name.fullNameStyle = bestGuess
      return
    }

    const guess = this.guessFullNameStyleWithName(name.familyName)
    if (guess !== FullNameStyle.UNDEFINED) {
      if (guess !== FullNameStyle.KJC && guess !== FullNameStyle.WESTERN) {
        name.fullNameStyle = guess
        return
      }
      bestGuess = guess
    } else {
      bestGuess = FullNameStyle.WESTERN
    }

    name.fullNameStyle = bestGuess
  }

  guessFullNameStyleWithName(name: string | null): FullNameStyle {
    if (!name || name.length === 0) {
      return FullNameStyle.UNDEFINED
    }

    // 문자만 남기기
    const temp = name.replace(/[^\p{L}]/gu, '')
    if (temp.length === 0) {
      return FullNameStyle.UNDEFINED
    }

    if (this.isContainKorean(name)) {
      return FullNameStyle.KOREAN
    }
    if (this.isContainJapanese(name)) {
      return FullNameStyle.JAPANESE
    }
    if (this.isContainChinese(name)) {
      return FullNameStyle.CHINESE
    }
    return FullNameStyle.WESTERN
  }

  guessPhoneticNameStyle(name: NameObj): void {
    if (name.phoneticNameStyle !== PhoneticNameStyle.UNDEFINED) {
      return
    }

    let bestGuess = this.guessPhoneticNameStyleWithName(name.phoneticFamilyName)
    if (bestGuess !== PhoneticNameStyle.UNDEFINED && bestGuess !== PhoneticNameStyle.KCJ) {
      name.phoneticNameStyle = bestGuess
      return
    }

    let guess = this.guessPhoneticNameStyleWithName(name.phoneticGivenName)
    if (guess !== PhoneticNameStyle.UNDEFINED) {
      if (guess !== PhoneticNameStyle.KCJ) {
        name.phoneticNameStyle = guess
        return
      }
      bestGuess = guess
    }

    guess = this.guessPhoneticNameStyleWithName(name.phoneticMiddleName)
    if (guess !== PhoneticNameStyle.UNDEFINED) {
      if (guess !== PhoneticNameStyle.KCJ) {
        name.phoneticNameStyle = guess
        return
      }
      bestGuess = guess
    }
  }

  guessPhoneticNameStyleWithName(name: string | null): PhoneticNameStyle {
    if (!name || name.length === 0) {
      return PhoneticNameStyle.UNDEFINED
    }

    if (this.isContainKorean(name)) {
      return PhoneticNameStyle.KOREAN
    }
    if (this.isContainJapanese(name)) {
      return PhoneticNameStyle.JAPANESE
    }
    if (this.isContainChinese(name)) {
      return PhoneticNameStyle.CHINESE
    }
    return PhoneticNameStyle.PINYIN
  }

  adjustedFullNameStyle(nameStyle: FullNameStyle): FullNameStyle {
    if (nameStyle === FullNameStyle.UNDEFINED) {
      if (this.language === 'ko') {
        return FullNameStyle.KOREAN
      } else if (this.language === 'ja') {
        return FullNameStyle.JAPANESE
      } else if (this.language === 'zh') {
        return FullNameStyle.CHINESE
      } else {
        return FullNameStyle.WESTERN
      }
    } else if (nameStyle === FullNameStyle.KJC) {
      if (this.language === 'ja') {
        return FullNameStyle.JAPANESE
      } else if (this.language === 'zh') {
        return FullNameStyle.CHINESE
      } else {
        return FullNameStyle.KOREAN
      }
    }
    return nameStyle
  }

  adjustedNameStyle(nameStyle: FullNameStyle, phoneticNameStyle: PhoneticNameStyle): FullNameStyle {
    if (phoneticNameStyle !== PhoneticNameStyle.UNDEFINED) {
      if (nameStyle === FullNameStyle.UNDEFINED || nameStyle === FullNameStyle.KJC) {
        if (phoneticNameStyle === PhoneticNameStyle.JAPANESE) {
          return FullNameStyle.JAPANESE
        } else if (phoneticNameStyle === PhoneticNameStyle.KOREAN) {
          return FullNameStyle.KOREAN
        }
        if (nameStyle === FullNameStyle.KJC && phoneticNameStyle === PhoneticNameStyle.PINYIN) {
          return FullNameStyle.CHINESE
        }
      }
    }
    return nameStyle
  }

  // 문자열 판별 함수
  isContainKorean(str: string): boolean {
    const pattern = /[\u1100-\u11FF\u3130-\u318F\uAC00-\uD7AF]/
    return pattern.test(str)
  }

  isContainJapanese(str: string): boolean {
    const pattern = /[\u30A0-\u30FF\u3040-\u309F\uFF00-\uFFEF]/
    return pattern.test(str)
  }

  isContainChinese(str: string): boolean {
    const pattern =
      /[\u2E80-\u2E99\u2E9B-\u2EF3\u2F00-\u2FD5\u3005\u3007\u3021-\u3029\u3038-\u303B\u3400-\u4DB5\u4E00-\u9FD5\uF900-\uFA6D\uFA70-\uFAD9]/
    return pattern.test(str)
  }

  // 이름 분할 함수
  splitWithName(name: NameObj, fullName: string, fullNameStyle: FullNameStyle | null = null): void {
    if (!fullName || fullName.length === 0) {
      return
    }

    if (fullNameStyle === null) {
      fullNameStyle = this.guessFullNameStyleWithName(fullName)
      if (fullNameStyle === FullNameStyle.KJC) {
        fullNameStyle = this.adjustedFullNameStyle(fullNameStyle)
      }
    }

    name.fullNameStyle = fullNameStyle

    switch (fullNameStyle) {
      case FullNameStyle.KOREAN:
        this.splitKoreanName(name, fullName)
        break
      case FullNameStyle.CHINESE:
        this.splitChineseName(name, fullName)
        break
      case FullNameStyle.JAPANESE:
        this.splitJapaneseName(name, fullName)
        break
      default:
        this.splitWesternName(name, fullName)
        break
    }
  }

  splitWesternName(name: NameObj, fullName: string): void {
    const tokens = new NameTokenizer(fullName)
    this.parsePrefix(name, tokens)

    // 이름이 한 개 또는 두 개의 토큰으로만 구성된 경우, 접미사가 아닌 이름/성으로 취급합니다.
    // 예: John Ma; Ma는 "M.A."가 아닌 성입니다.
    if (tokens.endPointer > 2) {
      this.parseSuffix(name, tokens)
    }

    if (!name.prefix && tokens.endPointer - tokens.startPointer === 1) {
      name.givenNames = tokens.tokens[tokens.startPointer]
    } else {
      this.parseLastName(name, tokens)
      this.parseMiddleName(name, tokens)
      this.parseGivenNames(name, tokens)
    }
  }

  splitChineseName(name: NameObj, fullName: string): void {
    const tokens = fullName.split(' ')

    for (const token of tokens) {
      if (!name.givenNames) {
        name.givenNames = token
      } else if (!name.familyName) {
        name.familyName = name.givenNames
        name.givenNames = token
      } else if (!name.middleName) {
        name.middleName = name.givenNames
        name.givenNames = token
      } else {
        name.middleName += name.givenNames
        name.givenNames = token
      }
    }

    // 단일 단어인 경우 해당 단어를 분석합니다.
    if (name.givenNames && !name.familyName && !name.middleName) {
      const length = fullName.length
      if (length === 2) {
        name.familyName = fullName.substring(0, 1)
        name.givenNames = fullName.substring(1)
      } else if (length === 3) {
        name.familyName = fullName.substring(0, 1)
        name.middleName = fullName.substring(1, 2)
        name.givenNames = fullName.substring(2)
      } else if (length === 4) {
        name.familyName = fullName.substring(0, 2)
        name.middleName = fullName.substring(2, 3)
        name.givenNames = fullName.substring(3)
      }
    }
  }

  splitJapaneseName(name: NameObj, fullName: string): void {
    const tokens = fullName.split(' ')

    for (const token of tokens) {
      if (!name.givenNames) {
        name.givenNames = token
      } else if (!name.familyName) {
        name.familyName = name.givenNames
        name.givenNames = token
      } else {
        name.givenNames += ' ' + token
      }
    }
  }

  splitKoreanName(name: NameObj, fullName: string): void {
    const tokens = fullName.split(' ')
    if (tokens.length > 1) {
      // 각 이름은 구분자로 식별될 수 있습니다.
      for (const token of tokens) {
        if (!name.givenNames) {
          name.givenNames = token
        } else if (!name.familyName) {
          name.familyName = name.givenNames
          name.givenNames = token
        } else {
          name.givenNames += ' ' + token
        }
      }
    } else {
      // 구분자가 없습니다. 성을 추측해 봅니다.
      // 대부분의 성은 길이가 1입니다.
      let familyNameLength = 1

      // 2글자 성과 비교합니다.
      for (const twoLengthFamilyName of KR_TWO_CHAR_FAMILY_NAMES) {
        if (fullName.startsWith(twoLengthFamilyName)) {
          familyNameLength = 2
          break
        }
      }

      name.familyName = fullName.substring(0, familyNameLength)
      if (fullName.length > familyNameLength) {
        name.givenNames = fullName.substring(familyNameLength)
      }
    }
  }

  // 파싱 관련 함수들
  parsePrefix(name: NameObj, tokens: NameTokenizer): void {
    if (tokens.startPointer === tokens.endPointer) {
      return
    }

    const firstToken = tokens.tokens[tokens.startPointer]
    if (this.prefixesSet.has(firstToken.toUpperCase())) {
      if (tokens.hasDot(tokens.startPointer)) {
        name.prefix = firstToken + '.'
      } else {
        name.prefix = firstToken
      }
      tokens.startPointer++
    }
  }

  parseSuffix(name: NameObj, tokens: NameTokenizer): void {
    if (tokens.startPointer === tokens.endPointer) {
      return
    }

    let lastToken = tokens.tokens[tokens.endPointer - 1]

    // 명시적 쉼표 구분 접미사 처리
    if (tokens.endPointer - tokens.startPointer > 2 && tokens.hasComma(tokens.endPointer - 2)) {
      if (tokens.hasDot(tokens.endPointer - 1)) {
        lastToken = lastToken + '.'
      }
      name.suffix = lastToken
      tokens.endPointer--
      return
    }

    if (lastToken.length > this.maxSuffixLength) {
      return
    }

    const normalized = lastToken.toUpperCase()
    if (this.suffixesSet.has(normalized)) {
      name.suffix = lastToken
      tokens.endPointer--
      return
    }

    const normalizedWithDot = normalized + '.'
    if (tokens.hasDot(tokens.endPointer - 1)) {
      lastToken = lastToken + '.'
    }

    // M.D.나 D.D.S.와 같은 접미사 처리
    let pos = tokens.endPointer - 1
    while (lastToken.length <= this.maxSuffixLength) {
      if (this.suffixesSet.has(normalizedWithDot)) {
        name.suffix = lastToken
        tokens.endPointer = pos
        return
      }

      if (pos === tokens.startPointer) {
        break
      }

      pos--
      if (tokens.hasDot(pos)) {
        lastToken = tokens.tokens[pos] + '.' + lastToken
      } else {
        lastToken = tokens.tokens[pos] + ' ' + lastToken
      }
    }
  }

  parseLastName(name: NameObj, tokens: NameTokenizer): void {
    if (tokens.startPointer === tokens.endPointer) {
      return
    }

    // 첫 단어 뒤에 쉼표가 있으면 성으로 간주합니다
    if (tokens.hasComma(tokens.startPointer)) {
      name.familyName = tokens.tokens[tokens.startPointer]
      tokens.startPointer++
      return
    }

    // 두 번째 단어 뒤에 쉼표가 있고 첫 번째 단어가
    // "de Sade"나 "von Cliburn"처럼 성 접두사인 경우
    // 첫 두 단어를 성으로 취급합니다.
    if (
      tokens.startPointer + 1 < tokens.endPointer &&
      tokens.hasComma(tokens.startPointer + 1) &&
      this.isFamilyNamePrefix(tokens.tokens[tokens.startPointer])
    ) {
      let familyNamePrefix = tokens.tokens[tokens.startPointer]
      if (tokens.hasDot(tokens.startPointer)) {
        familyNamePrefix = familyNamePrefix + '.'
      }
      name.familyName = familyNamePrefix + ' ' + tokens.tokens[tokens.startPointer + 1]
      tokens.startPointer += 2
      return
    }

    // 마지막으로, 마지막 단어를 성으로 간주합니다
    name.familyName = tokens.tokens[tokens.endPointer - 1]
    tokens.endPointer--

    // "de Sade"나 "von Cliburn"과 같은 성 처리
    if (tokens.endPointer - tokens.startPointer > 0) {
      const lastNamePrefix = tokens.tokens[tokens.endPointer - 1]
      if (this.isFamilyNamePrefix(lastNamePrefix)) {
        if (tokens.hasDot(tokens.endPointer - 1)) {
          name.familyName = lastNamePrefix + '.' + ' ' + name.familyName
        } else {
          name.familyName = lastNamePrefix + ' ' + name.familyName
        }
        tokens.endPointer--
      }
    }
  }

  isFamilyNamePrefix(word: string): boolean {
    if (!word) return false

    const normalized = word.toUpperCase()
    return (
      this.lastNamePrefixesSet.has(normalized) || this.lastNamePrefixesSet.has(normalized + '.')
    )
  }

  parseMiddleName(name: NameObj, tokens: NameTokenizer): void {
    if (tokens.startPointer === tokens.endPointer) {
      return
    }

    if (tokens.endPointer - tokens.startPointer > 1) {
      if (
        tokens.endPointer - tokens.startPointer === 2 ||
        !this.conjunctions.has(tokens.tokens[tokens.endPointer - 2].toUpperCase())
      ) {
        name.middleName = tokens.tokens[tokens.endPointer - 1]
        if (tokens.hasDot(tokens.endPointer - 1)) {
          name.middleName = name.middleName + '.'
        }
        tokens.endPointer--
      }
    }
  }

  parseGivenNames(name: NameObj, tokens: NameTokenizer): void {
    if (tokens.startPointer === tokens.endPointer) {
      return
    }

    if (tokens.endPointer - tokens.startPointer === 1) {
      name.givenNames = tokens.tokens[tokens.startPointer]
    } else {
      let sb = ''
      for (let i = tokens.startPointer; i < tokens.endPointer; i++) {
        if (i !== tokens.startPointer) {
          sb += ' '
        }
        sb += tokens.tokens[i]
        if (tokens.hasDot(i)) {
          sb += '.'
        }
      }
      name.givenNames = sb
    }
  }

  // 이름 조합 함수
  joinWithName(
    name: NameObj,
    givenNameFirst: boolean = false,
    includePrefix: boolean = false,
  ): string {
    const prefix = includePrefix ? name.prefix : null

    switch (name.fullNameStyle) {
      case FullNameStyle.KJC:
      case FullNameStyle.CHINESE:
      case FullNameStyle.KOREAN:
        return this._joinWithPrefix(
          prefix,
          name.familyName,
          name.middleName,
          name.givenNames,
          name.suffix,
          false,
          false,
          false,
        )

      case FullNameStyle.JAPANESE:
        return this._joinWithPrefix(
          prefix,
          name.familyName,
          name.middleName,
          name.givenNames,
          name.suffix,
          true,
          false,
          false,
        )

      default:
        if (givenNameFirst) {
          return this._joinWithPrefix(
            prefix,
            name.givenNames,
            name.middleName,
            name.familyName,
            name.suffix,
            true,
            false,
            true,
          )
        } else {
          return this._joinWithPrefix(
            prefix,
            name.familyName,
            name.givenNames,
            name.middleName,
            name.suffix,
            true,
            true,
            true,
          )
        }
    }
  }

  joinPhoneticName(name: NameObj): string {
    return this._joinWithPrefix(
      null,
      name.phoneticFamilyName,
      name.phoneticMiddleName,
      name.phoneticGivenName,
      null,
      true,
      false,
      false,
    )
  }

  _joinWithPrefix(
    prefix: string | null,
    part1: string | null,
    part2: string | null,
    part3: string | null,
    suffix: string | null,
    useSpace: boolean,
    useCommaAfterPart1: boolean,
    useCommaAfterPart3: boolean,
  ): string {
    const hasPrefix = prefix && prefix.length > 0
    const hasPart1 = part1 && part1.length > 0
    const hasPart2 = part2 && part2.length > 0
    const hasPart3 = part3 && part3.length > 0
    const hasSuffix = suffix && suffix.length > 0

    let isSingleWord = true
    let singleWord: string | null = null

    if (hasPrefix) {
      singleWord = prefix
    }

    if (hasPart1) {
      if (singleWord && singleWord.length > 0) {
        isSingleWord = false
      } else {
        singleWord = part1
      }
    }

    if (hasPart2) {
      if (singleWord && singleWord.length > 0) {
        isSingleWord = false
      } else {
        singleWord = part2
      }
    }

    if (hasPart3) {
      if (singleWord && singleWord.length > 0) {
        isSingleWord = false
      } else {
        singleWord = part3
      }
    }

    if (hasSuffix) {
      if (singleWord && singleWord.length > 0) {
        isSingleWord = false
      } else {
        singleWord = this.normalizedSuffix(suffix)
      }
    }

    if (isSingleWord) {
      return singleWord || ''
    }

    let sb = ''

    if (hasPrefix) {
      sb += prefix
    }

    if (hasPart1) {
      if (hasPrefix) {
        sb += ' '
      }
      sb += part1
    }

    if (hasPart2) {
      if (hasPrefix || hasPart1) {
        if (useCommaAfterPart1) {
          sb += ','
        }
        if (useSpace) {
          sb += ' '
        }
      }
      sb += part2
    }

    if (hasPart3) {
      if (hasPrefix || hasPart1 || hasPart2) {
        if (useSpace) {
          sb += ' '
        }
      }
      sb += part3
    }

    if (hasSuffix) {
      if (hasPrefix || hasPart1 || hasPart2 || hasPart3) {
        if (useCommaAfterPart3) {
          sb += ','
        }
        if (useSpace) {
          sb += ' '
        }
      }
      sb += this.normalizedSuffix(suffix)
    }

    return sb
  }

  normalizedSuffix(suffix: string | null): string {
    if (!suffix) return suffix || ''

    const length = suffix.length
    if (length === 0 || suffix.charAt(length - 1) === '.') {
      return suffix
    }

    const withDot = suffix + '.'
    if (this.suffixesSet.has(withDot.toUpperCase())) {
      return withDot
    } else {
      return suffix
    }
  }
}

// 기본 접두사, 접미사, 성 접두사, 접속사 문자열
export const DEFAULT_COMMON_PREFIX = 'Mr,Ms,Mrs,Dr,Prof'
export const DEFAULT_COMMON_SUFFIX = 'Jr,Sr,I,II,III,IV,V,MD,PhD,JD,DDS,EdD,DVM'
export const DEFAULT_COMMON_LAST_NAME_PREFIX =
  'von,van,de,del,della,di,da,la,le,Lo,der,den,bin,binti'
export const DEFAULT_COMMON_CONJUNCTION = 'and,or,y,&'
